import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, IconButton, Card, Chip, Paper, Button } from '@mui/material';
import { ChevronLeft, ChevronRight, EventAvailable, EventBusy } from '@mui/icons-material';
import moment from 'moment';
import './RecurringEventCalendar.scss';

function RecurringEventCalendar({ selectedDates, onDatesChange, existingEvents }) {
  const [currentMonth, setCurrentMonth] = useState(moment());

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  // Get all days for the current month view (including padding days from prev/next month)
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week'); // Start from Sunday
    const endDate = endOfMonth.clone().endOf('week'); // End on Saturday

    const days = [];
    const day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  }, [currentMonth]);

  // Group existing events by date
  const eventsByDate = useMemo(() => {
    const grouped = {};
    existingEvents?.forEach((event) => {
      const eventDate = event.parameters_event?.event_date;
      if (eventDate) {
        const dateKey = moment(eventDate).format('YYYY-MM-DD');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(event);
      }
    });
    return grouped;
  }, [existingEvents]);

  // Check if a date is selected
  const isDateSelected = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return selectedDates.includes(dateStr);
  };

  // Check if a date has existing events
  const hasExistingEvents = (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return eventsByDate[dateKey]?.length > 0;
  };

  // Check if a date is in the past
  const isPastDate = (date) => date.isBefore(moment(), 'day');

  // Check if a date is in current month
  const isCurrentMonth = (date) => date.month() === currentMonth.month();

  // Toggle date selection
  const toggleDate = (date) => {
    if (isPastDate(date) || !isCurrentMonth(date)) return;

    const dateStr = date.format('YYYY-MM-DD');
    if (isDateSelected(date)) {
      onDatesChange(selectedDates.filter((d) => d !== dateStr));
    } else {
      onDatesChange([...selectedDates, dateStr]);
    }
  };

  // Clear all selections
  const clearAllSelections = () => {
    onDatesChange([]);
  };

  // Get the count of events on a specific date
  const getEventCount = (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return eventsByDate[dateKey]?.length || 0;
  };

  // Weekday headers
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box className="recurring-event-calendar">
      {/* Calendar Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: 'rgba(238, 123, 87, 0.05)' }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <IconButton onClick={previousMonth} size="small">
              <ChevronLeft />
            </IconButton>
          </Grid>
          <Grid item>
            <Typography variant="h6" fontWeight="bold" color="#EE7B57">
              {currentMonth.format('MMMM YYYY')}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={nextMonth} size="small">
              <ChevronRight />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Selection Info & Legend Combined */}
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>{selectedDates.length}</strong> date{selectedDates.length !== 1 ? 's' : ''}{' '}
            selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: '#EE7B57',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption">Selected</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: '#3c99ba',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption">Events</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: '#E0E0E0',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption">Past</Typography>
            </Box>
          </Box>
        </Box>
        {selectedDates.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={clearAllSelections}
            startIcon={<EventBusy />}
            sx={{ py: 0.5 }}>
            Clear All
          </Button>
        )}
      </Box>

      {/* Calendar Grid */}
      <Card elevation={2}>
        <Box sx={{ p: 1.5 }}>
          {/* Weekday Headers */}
          <Grid container spacing={0} sx={{ mb: 0.5 }}>
            {weekDays.map((day) => (
              <Grid
                item
                xs={12 / 7}
                key={day}
                sx={{
                  textAlign: 'center',
                  py: 0.5,
                  fontWeight: 'bold',
                  color: '#666',
                }}>
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days */}
          <Grid container spacing={0}>
            {calendarDays.map((day) => {
              const selected = isDateSelected(day);
              const hasEvents = hasExistingEvents(day);
              const isPast = isPastDate(day);
              const isCurrentMonthDay = isCurrentMonth(day);
              const eventCount = getEventCount(day);

              // Determine background color
              let bgColor = 'white';
              if (selected) {
                bgColor = 'rgba(238, 123, 87, 0.1)';
              } else if (isPast) {
                bgColor = '#F5F5F5';
              } else if (hasEvents) {
                bgColor = 'rgba(78, 205, 196, 0.1)';
              }

              // Determine hover background color
              let hoverBgColor;
              if (!isPast && isCurrentMonthDay) {
                hoverBgColor = selected ? 'rgba(238, 123, 87, 0.2)' : 'rgba(238, 123, 87, 0.05)';
              }

              // Determine text color
              let textColor = '#CCC';
              if (selected) {
                textColor = '#EE7B57';
              } else if (isPast) {
                textColor = '#999';
              } else if (isCurrentMonthDay) {
                textColor = '#333';
              }

              return (
                <Grid
                  item
                  xs={12 / 7}
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    p: 0.3,
                  }}>
                  <Box
                    onClick={() => toggleDate(day)}
                    sx={{
                      height: '50px',
                      minHeight: '50px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isPast || !isCurrentMonthDay ? 'not-allowed' : 'pointer',
                      borderRadius: 1,
                      border: selected ? '2px solid #EE7B57' : '1px solid #E0E0E0',
                      bgcolor: bgColor,
                      opacity: !isCurrentMonthDay ? 0.3 : 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: hoverBgColor,
                        transform: !isPast && isCurrentMonthDay ? 'scale(1.05)' : undefined,
                      },
                    }}>
                    <Typography
                      variant="body2"
                      fontWeight={selected ? 'bold' : 'normal'}
                      sx={{
                        fontSize: '0.85rem',
                        color: textColor,
                      }}>
                      {day.format('D')}
                    </Typography>
                    {hasEvents && (
                      <Chip
                        label={eventCount}
                        size="small"
                        sx={{
                          height: 14,
                          fontSize: '0.6rem',
                          bgcolor: '#3c99ba',
                          color: 'white',
                          mt: 0.3,
                          minWidth: '20px',
                          '& .MuiChip-label': {
                            px: 0.5,
                          },
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Card>

      {/* Selected Dates List */}
      {selectedDates.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Selected Dates
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {selectedDates.map((dateStr) => (
              <Chip
                key={dateStr}
                label={moment(dateStr).format('MMM DD, YYYY')}
                onDelete={() => onDatesChange(selectedDates.filter((d) => d !== dateStr))}
                color="primary"
                size="small"
                sx={{
                  bgcolor: '#EE7B57',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

RecurringEventCalendar.propTypes = {
  selectedDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDatesChange: PropTypes.func.isRequired,
  existingEvents: PropTypes.arrayOf(
    PropTypes.shape({
      parameters_event: PropTypes.shape({
        event_date: PropTypes.string,
      }),
    }),
  ),
};

RecurringEventCalendar.defaultProps = {
  existingEvents: [],
};

export default RecurringEventCalendar;
